import PropTypes from 'prop-types';
import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useClick,
  useDismiss,
  useRole,
  useInteractions,
  FloatingPortal,
} from '@floating-ui/react';

const SearchableSelect = ({
  name,
  options,
  defaultValue,
  onChange,
  className = '',
  bordered = true,
  registerOptions = {},
  disabled = false,
  maxVisibleItems = 8,
}) => {
  const { register, setValue, watch } = useFormContext();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownWidth, setDropdownWidth] = useState('auto');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const searchInputRef = useRef(null);
  const listRef = useRef(null);
  const sizeClass = className.match('select-') ? '' : 'select-xs';
  const inputType = bordered ? 'select-bordered' : '';

  const currentValue = watch(name) || defaultValue || '';

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: 'bottom-start',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(5),
      flip({
        fallbackPlacements: ['bottom-end', 'top-start'],
        fallbackStrategy: 'bestFit',
      }),
      shift({ padding: 5 }),
    ],
  });

  useEffect(() => {
    if (isOpen && refs.reference.current) {
      const element = refs.reference.current;
      if (element && typeof element === 'object' && 'offsetWidth' in element) {
        setDropdownWidth(String(element.offsetWidth));
      }
    }
  }, [isOpen, refs]);

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    const term = searchTerm.toLowerCase();
    return options.filter(option => {
      const label = option.label?.toLowerCase() || '';
      const value = option.value?.toLowerCase() || '';
      return label.includes(term) || value.includes(term);
    });
  }, [options, searchTerm]);

  const selectedOption = options.find(opt => opt.value === currentValue);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [searchTerm]);

  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const element = listRef.current.children[highlightedIndex];
      if (element) {
        element.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [highlightedIndex]);

  const handleSelect = useCallback(
    option => {
      const event = { target: { value: option.value } };
      setValue(name, option.value, { shouldValidate: true });
      // eslint-disable-next-line react-hook-form/no-unregistered-onChange
      const registerOnChange = registerOptions && 'onChange' in registerOptions ? registerOptions.onChange : null;
      if (registerOnChange && typeof registerOnChange === 'function') {
        registerOnChange(event);
      }
      if (onChange && typeof onChange === 'function') {
        onChange(event);
      }
      setIsOpen(false);
      setSearchTerm('');
      setHighlightedIndex(-1);
    },
    [name, setValue, registerOptions, onChange],
  );

  const handleKeyDown = useCallback(
    e => {
      if (filteredOptions.length === 0) {
        if (e.key === 'Escape') {
          setIsOpen(false);
          setSearchTerm('');
          setHighlightedIndex(-1);
        }
        return;
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex(prev => {
            const newIndex = prev < filteredOptions.length - 1 ? prev + 1 : prev;
            return newIndex;
          });
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex(prev => {
            const newIndex = prev > 0 ? prev - 1 : -1;
            return newIndex;
          });
          break;
        case 'Enter':
          e.preventDefault();
          if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
            handleSelect(filteredOptions[highlightedIndex]);
          } else if (filteredOptions.length === 1) {
            handleSelect(filteredOptions[0]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          setIsOpen(false);
          setSearchTerm('');
          setHighlightedIndex(-1);
          break;
      }
    },
    [filteredOptions, highlightedIndex, handleSelect],
  );

  return (
    <div className="relative w-full">
      <input type="hidden" {...register(name, registerOptions)} />
      <div
        ref={refs.setReference}
        {...getReferenceProps()}
        className={`select ${sizeClass} ${inputType} ${className} ${disabled ? 'select-disabled' : ''} cursor-pointer`}
        onClick={() => !disabled && setIsOpen(true)}
        tabIndex={disabled ? -1 : 0}
        onKeyDown={e => {
          if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
            e.preventDefault();
            setIsOpen(true);
          }
        }}
      >
        {selectedOption ? selectedOption.label : 'Select...'}
      </div>

      {isOpen && !disabled && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            {...getFloatingProps()}
            className="z-[1001] bg-base-100 rounded-md shadow-lg border border-gray-200 overflow-hidden"
            style={{ ...floatingStyles, width: dropdownWidth }}
          >
            <div className="p-2 border-b border-gray-200">
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Search..."
                className="input input-bordered input-sm w-full"
                onClick={e => e.stopPropagation()}
                autoFocus
              />
            </div>
            <ul
              ref={listRef}
              className="py-1 overflow-y-auto"
              style={{ maxHeight: `${maxVisibleItems * 2.5}rem` }}
            >
              {filteredOptions.length === 0 ? (
                <li className="px-4 py-2 text-sm text-gray-500 text-center">No options found</li>
              ) : (
                filteredOptions.map((option, index) => (
                  <li
                    key={option.value}
                    className={`px-4 py-2 hover:bg-gray-100 cursor-pointer ${
                      option.value === currentValue ? 'bg-gray-50 font-medium' : ''
                    } ${index === highlightedIndex ? 'bg-blue-50' : ''}`}
                    onClick={() => handleSelect(option)}
                    onMouseEnter={() => setHighlightedIndex(index)}
                  >
                    {option.label}
                  </li>
                ))
              )}
            </ul>
          </div>
        </FloatingPortal>
      )}
    </div>
  );
};

SearchableSelect.propTypes = {
  name: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.any.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  defaultValue: PropTypes.any,
  className: PropTypes.string,
  onChange: PropTypes.func,
  bordered: PropTypes.bool,
  registerOptions: PropTypes.object,
  disabled: PropTypes.bool,
  maxVisibleItems: PropTypes.number,
};

export default SearchableSelect;

